import type { RevenueToolSlug } from "@/lib/revenue-tool-pages";

export const COMPACT_WIDTH = 358;
export const COMPACT_BELOW = 440;
const BLEED = 1.36;

export type ToolDemoSpec = {
  label: string;
  frame: "column" | "bleed";
  width: number;
  height: number;
  compact: { width: typeof COMPACT_WIDTH; height: number };
};

export function stageWidth({ frame, width }: ToolDemoSpec): number {
  return frame === "bleed" ? Math.ceil(width * (1 + BLEED)) : width;
}

const WHALE_WATCH_SCENE =
  "on Monday Night Football, a $493,498 whale bet at Kalshi on the Seattle Seahawks -3.5 at +115 lands beside bets of $26,537 at Crypto.com and $458 at ProphetX, bringing the whale money behind the play from $27.0k to $520.5k at a +0.9% edge. Novig, a betting exchange, still offers +117, while DraftKings and FanDuel sit at -104.";

const WHALE_WATCH_STAGE = {
  frame: "bleed",
  width: 560,
  height: 450,
  compact: { width: 358, height: 676 },
} as const;

export const TOOL_DEMOS = {
  "whale-watch": { label: `Whale Watch example: ${WHALE_WATCH_SCENE}`, ...WHALE_WATCH_STAGE },
  "prediction-markets": { label: `Prediction Market Analytics example: ${WHALE_WATCH_SCENE}`, ...WHALE_WATCH_STAGE },
  "esports-discrepancies": {
    label:
      "Prop Discrepancies example: on Monday Night Football, D'Andre Swift's rush plus receiving yards line runs from 81.5 at DK Pick6 and 2 other apps to 88.5 at Betr and 1 other app, a 7.00 gap and an 8.6% edge, ranked above his rushing yards, where 59.5 to 61.5 is a 2.00 gap and a 3.4% edge. Expanding the row shows one tile per line: 81.5 is the easiest over, ParlayPlay and 1 other app sit at 83.5, Boom at 84.5, and 88.5 is the easiest under, so the best play is under 88.5 at Betr.",
    frame: "column",
    width: 560,
    height: 604,
    compact: { width: 358, height: 560 },
  },
  "steam-chaser": {
    label:
      "Steam Chaser example: on Sunday Night Football, ProphetX moves Jayden Daniels over 19.5 rushing yards from -423 to -571, and FanDuel and Caesars move too. Novig, a betting exchange, still pays the old -400, against fair odds of -464, a +2.8% EV play. Below it, Novig still has Jeremy McNichols over 19.5 rushing yards at +104 after FanDuel moved from -114 to -125 (+2.7% EV), and FanDuel still has Jack Eichel under 2.5 shots on goal at +110 after Novig moved from +117 to +100 (+2.4% EV).",
    frame: "column",
    width: 560,
    height: 624,
    compact: { width: 358, height: 620 },
  },
  arbitrage: {
    label:
      "Arbitrage example: Kris Dunn rebounds plus assists, under 10.5 at -140 at BetSaracen (+5.6% EV) and over 10.5 at +168 at FanDuel. Stakes of $608 and $389 get $1,042 back either way, a $45.29 profit on $997 (4.55%). Typing $500 as the FanDuel stake fills in $782 at BetSaracen, so either result pays $1,340, a $58.00 profit on $1,282 (4.52%).",
    frame: "column",
    width: 560,
    height: 692,
    compact: { width: 358, height: 590 },
  },
  "low-hold": {
    label:
      "Low Hold example: two markets ranked by hold. Luka Doncic first-quarter points has a 0.00% hold across 9 books, under 11.5 at -125 at Hard Rock and over 11.5 at +125 at Fanatics, so $556 and $444 cost $1.00 on $1,000. Thomas Chabot powerplay points below it also holds 0.00%. Opening Luka's card slides up a sheet that shows more books' prices for each side, and the best prices save $63.00 against both sides at Hard Rock alone.",
    frame: "column",
    width: 560,
    height: 693,
    compact: { width: 358, height: 594 },
  },
  "ev-sniper": {
    label:
      "+EV Sniper example: Saquon Barkley over 0.5 rushing plus receiving touchdowns at Betr +256 tops the board ranked by EV at +25.6%, with a 35.3% chance to hit and an estimated fair price of +183. Expanding the row shows DraftKings at +175, FanDuel at +170, Caesars at +167 and Pinnacle at +164.",
    frame: "bleed",
    width: 560,
    height: 520,
    compact: { width: 358, height: 395 },
  },
  "odds-screen": {
    label:
      "Odds Screen example: on Sunday Night Football, Eagles @ Commanders, clicking the Washington Commanders moneyline opens Compare books with the books' prices best first across 34 books. Novig, a betting exchange, has the best price at -233, ahead of 4casters and SX Bet at -236, while DraftKings sits last at -278, so winning $100 costs $233 at Novig or $278 at DraftKings. On the Eagles side Courtside is best at +230.",
    frame: "bleed",
    width: 560,
    height: 600,
    compact: { width: 358, height: 560 },
  },
  "ev-props-optimizer": {
    label:
      "Fantasy Props example: on Underdog, Shai Gilgeous-Alexander over 30.5 points has a 56.4% chance to hit from FanDuel at -145 and DraftKings at -142, above the 53.8% a 6 Pick Insured needs, with Karl-Anthony Towns over 11.5 rebounds next at 55.2%. Opening the row shows each book's over and under prices at the same 30.5 line.",
    frame: "column",
    width: 560,
    height: 500,
    compact: { width: 358, height: 486 },
  },
  "prizepicks-optimizer": {
    label:
      "PrizePicks Optimizer example: on PrizePicks, Luka Doncic over 32.5 points has a 56.0% chance to hit, above the 54.3% each pick needs in a 6 Pick Flex, from FanDuel at -142 and DraftKings at -143. Opening +EV Slips shows the best slip on the board: a 6 Pick Flex at +7.2% EV paying 25x, with Luka Doncic, Saquon Barkley, Jalen Brunson, Stephen Curry, Caleb Williams and Donovan Mitchell.",
    frame: "column",
    width: 560,
    height: 500,
    compact: { width: 358, height: 560 },
  },
  "sports-betting-optimizer": {
    label:
      "Sports Betting Optimizer example: with FanDuel picked as the book, its best-priced NBA and NFL bets show edges of +2.8% EV and less. Switching the book to Fanatics re-ranks the board with Cooper Kupp over 3.5 receptions at +160 (+8.2% EV) and the Golden State Warriors -12.5 spread at +430 (+4.8% EV and a 19.8% chance to hit). Opening the Warriors row shows the same bet at DraftKings for +342 and FanDuel for +330, so $100 wins $430 at Fanatics and $342 at DraftKings.",
    frame: "bleed",
    width: 560,
    height: 480,
    compact: { width: 358, height: 470 },
  },
} satisfies Record<RevenueToolSlug, ToolDemoSpec>;

